import ErrorCode from '../../common/error-code';
import RtcError from '../../common/rtc-error';
import Logger from '../log/logger';
import loggerManager from '../log/logger-manager';

let seq = 0;
class FunctionQueue {
  log: Logger;
  isRunning = false;
  queue: QueueItem[] = [];

  constructor(id?: string) {
    let loggerId = `fq${++seq}`;
    if (id) loggerId += `|${id}`;
    this.log = loggerManager.createLogger({ id: loggerId });
  }
  get length() {
    return this.queue.length;
  }

  get lastQueueItem() {
    if (this.length === 0) return null;
    return this.queue[this.length - 1];
  }

  push(functionCallItem: FunctionCallItem, merge = false) {
    // @ts-ignore
    const queueItem: QueueItem = { ...functionCallItem };
    const promise = new Promise((resolve, reject) => {
      queueItem.resolve = resolve;
      queueItem.reject = reject;
    });
    queueItem.promise = promise;

    if (merge) {
      if (this.length <= 1) {
        this.queue.push(queueItem);
      } else {
        this.lastQueueItem?.promise?.then(queueItem.resolve, queueItem.reject);
      }
    } else {
      this.queue.push(queueItem);
    }
    this.log.debug(`push ${this.length}`, functionCallItem.funcName, functionCallItem.args);
    if (!this.isRunning) {
      this.callNext();
    }
    return promise;
  }

  shift() {
    const queueItem = this.queue.shift();
    this.log.debug(`shift ${this.length}`, queueItem?.funcName, queueItem?.args);
    return queueItem;
  }

  callNext() {
    if (this.isRunning || this.length === 0) return;

    const { fn, args, context, resolve, reject, funcName } = this.queue[0];
    this.log.debug('callNext', this.length, funcName, args);
    this.isRunning = true;
    fn.apply(context, args)
      .then(resolve, reject)
      .finally(() => {
        this.isRunning = false;
        this.shift();
        this.callNext();
      });
  }
}

interface FunctionCallItem { fn: (args: any) => any, args: any, context: any, funcName: string }
type DescriptorValueType = (...args: any) => Promise<any>;
type QueueItem = FunctionCallItem & { resolve: (args: any) => void, reject: (reason?: any) => void, promise?: Promise<any> };

const fnQueueMap: WeakMap<any, FunctionQueue> = new WeakMap(); // WeakMap<class instance, [{fn, args}, {fn, args}, {fn, args}]>
const fnWithArgsQueueMap: WeakMap<any, Map<any, FunctionQueue>> = new WeakMap(); // WeakMap<class instance, Map<argKey, [{fn, args}, {fn, args}, {fn, args}]>>
/**
 * 确保异步函数按顺序执行
 *
 * @param {boolean} merge 多次调用，是否合并为一个任务
 * @example
 * class A {
 *  @addCallQueue(true)
 *  funcA() {}
 *  @addCallQueue(false)
 *  funcB() {}
 * }
 *
 * const a = new A();
 * a.funcA();
 * a.funcA(); // 在上一个 funcA 执行结束后，才开始执行
 * a.funcB(); // 在上一个 funcB 执行结束后，才开始执行
 * a.funcA(); // 在上一个 funcA 执行结束后，才开始执行
 */
export function addCallQueue(merge = false) {
  return function (target: any, name: string, descriptor: TypedPropertyDescriptor<DescriptorValueType>) {
    const oldFn = descriptor.value!;

    descriptor.value = function (...args: any) {
      const queue = fnQueueMap.get(this) || new FunctionQueue();
      fnQueueMap.set(this, queue);
      return queue.push({ fn: oldFn, args, context: this, funcName: name }, merge);
    };
    return descriptor;
  };
}

/**
 * 从异步任务队列中移除任务，例如：
 * 1. publish video
 * 2. publish audio
 * 3. unpublish audio ------> 此时将排队中的 publish audio 移除
 * 4. unpublish video ------> 第一个任务 publish video 正在执行，不移除
 */
export function removeCallQueue(reason: string) {
  return function (target: any, name: string, descriptor: TypedPropertyDescriptor<DescriptorValueType>) {
    const oldFn = descriptor.value!;

    descriptor.value = function (...args: any[]) {
      const functionQueue = fnQueueMap.get(this);
      if (functionQueue) {
        const newQueue = functionQueue.queue.filter((queueItem, index) => {
          // 第一个任务是正在执行的，不过滤。
          if (index === 0) return true;
          let isSameArgs = true;
          // 之前的 args 中，有参数在当前的 args 中找不到，则认为不是相同的 args
          queueItem.args.forEach((argItem: any) => {
            if (!args.find(item => item === argItem)) {
              isSameArgs = false;
            }
          });
          if (isSameArgs) {
            queueItem.reject(new RtcError({
              code: ErrorCode.API_CALL_ABORTED,
              message: reason
            }));
            return false;
          }
          return true;
        });
        functionQueue.queue = newQueue;
      }
      return oldFn.apply(this, args);
    };
    return descriptor;
  };
}
export function clearCallQueue(reason: string) {
  return function (target: any, name: string, descriptor: TypedPropertyDescriptor<DescriptorValueType>) {
    const oldFn = descriptor.value!;

    descriptor.value = function (...args: any) {
      const abortList: QueueItem[] = [];
      fnQueueMap.get(this)?.queue?.forEach(item => abortList.push(item));
      fnWithArgsQueueMap.get(this)?.forEach(functionQueue => functionQueue?.queue.forEach(item => abortList.push(item)));
      abortList.forEach(item => {
        item.reject(new RtcError({
          code: ErrorCode.API_CALL_ABORTED,
          message: reason
        }));
      });
      fnQueueMap.delete(this);
      fnWithArgsQueueMap.delete(this);
      return oldFn.apply(this, args);
    };
    return descriptor;
  };
}

/**
 * 确保异步函数按顺序执行，根据函数的参数值来标识
 *
 *  * @example
 * class Room {
 *  // 确保相同 userId 的订阅、取消订阅是按顺序执行的
 *  @addCallQueueValidArgs((...remoteTracks) => remoteTracks[0].userId)
 *  subscribe(...remoteTracks) {
 *    ...
 *  }
 *
 *  @addCallQueueValidArgs((...remoteTracks) => remoteTracks[0].userId)
 *  unsubscribe(...remoteTracks) {
 *    ...
 *  }
 * }
 */
export function addCallQueueValidArgs(param: ((...args: any) => any), getQueueName?: (...args: any) => string) {
  return function (target: any, name: string, descriptor: TypedPropertyDescriptor<DescriptorValueType>) {
    const oldFn = descriptor.value!;

    const getArgToValidateValue = (args: any) => param(...args);

    descriptor.value = function (...args: any) {
      const map = fnWithArgsQueueMap.get(this) || new Map<any, FunctionQueue>();
      const queue = map.get(getArgToValidateValue(args)) || new FunctionQueue(getQueueName?.(...args));
      map.set(getArgToValidateValue(args), queue);
      fnWithArgsQueueMap.set(this, map);
      return queue.push({ fn: oldFn, args, context: this, funcName: name });
    };
    return descriptor;
  };
}

// TODO: removeCallQueueValidArgs
